import { EditableSelectOption } from 'components/common/EditableCell';
import {
  INPUT_STEP_LABEL_SEED_DRESS,
  SEED_NO_VARIETY_LABEL,
  SEED_TREATMENT_RATE_MODE_PER_100KG_LABEL,
  SEED_TREATMENT_RATE_MODE_PER_HA_LABEL,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput, IUnit } from 'interfaces/IInput';
import {
  IInputStep,
  IInputStepSeed,
  IInputStepSeedDress,
  InputStepType,
  SeedDressRateMode,
} from 'interfaces/IInputStep';
import {
  IInputTask,
  IInputTaskApplication,
  IInputTaskCropEstablishment,
  InputTaskType,
} from 'interfaces/IInputTask';
import {
  findLinkedSeedRateKg,
  resolveAreaHa,
} from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import { AREA_UNIT_FILTER, filterGUnitsByFilter } from 'utils/GUnitFilters';
import {
  convertMillilitresToLitres,
  isMillilitreUnit,
  resolveUnitKind,
} from 'utils/unitConversion';

import type { StepSelectorOption } from './stepConfig';
import { STEP_UNIT_FILTERS } from './stepFactory';

export const getTaskSteps = (task: IInputTask): IInputStep[] =>
  Array.isArray(task.steps) ? task.steps : [];

export const NO_CALCULATION_STEP_TYPES = new Set<InputStepType>([
  InputStepType.Production,
  InputStepType.Generic,
  InputStepType.Application,
]);

export const getStepRate = (step: IInputStep): number | null => {
  if ('rate' in step) {
    return step.rate ?? null;
  }
  return null;
};

export const getStepCost = (step: IInputStep): number | null => {
  if ('cost' in step) {
    return step.cost ?? null;
  }
  return null;
};

export const getStepUnitLabel = (step: IInputStep): string => {
  if ('appUnit' in step) {
    return step.appUnit?.unit || ' ';
  }
  if ('yieldUnit' in step) {
    return step.yieldUnit?.unit || ' ';
  }
  return ' ';
};

/**
 * The rate unit as the grid shows it: a seed treatment's unit carries its
 * rate mode, e.g. "g/100kg" or "g/ha", so it reads as what the rate is per
 * (OP-2464). The dropdown still lists the plain units.
 */
export const getStepRateUnitDisplayLabel = (step: IInputStep): string => {
  const unitLabel = getStepUnitLabel(step);
  if (step.stepType !== InputStepType.SeedDress || !unitLabel.trim()) {
    return unitLabel;
  }
  const rateModeLabel =
    step.rateMode === SeedDressRateMode.PerHa
      ? SEED_TREATMENT_RATE_MODE_PER_HA_LABEL
      : SEED_TREATMENT_RATE_MODE_PER_100KG_LABEL;
  return `${unitLabel}${rateModeLabel}`;
};

export const getStepAppliedUnit = (step: IInputStep): IUnit | null => {
  if ('appUnit' in step) return step.appUnit ?? null;
  if ('yieldUnit' in step) return step.yieldUnit ?? null;
  return null;
};

// Water Rate only makes sense once a liquid is actually being applied — seed
// treatment products are liquid but aren't mixed with water at a rate, so
// they're excluded (OP-2125).
export const taskHasLiquidInput = (
  task: IInputTask,
  gUnits: GUnit[]
): boolean =>
  task.steps.some(
    (step) =>
      step.stepType !== InputStepType.SeedDress &&
      resolveUnitKind(getStepAppliedUnit(step), gUnits) === 'liquid'
  );

// Products sprayed in water, the only ones a water rate is for (OP-2556).
// Seed treatments, fertiliser coatings and liquid fertilisers aren't.
const SPRAYED_STEP_TYPES = new Set<InputStepType>([
  InputStepType.CropProtection,
  InputStepType.Adjuvant,
  InputStepType.Biostimulant,
]);

/**
 * True when the task sprays a liquid product — plant protection, adjuvant or
 * biostimulant in a liquid unit — so it needs a water rate. Narrower than
 * taskHasLiquidInput, which also counts liquid fertilisers.
 */
export const taskNeedsWaterRate = (
  task: IInputTask,
  gUnits: GUnit[]
): boolean =>
  task.steps.some(
    (step) =>
      SPRAYED_STEP_TYPES.has(step.stepType) &&
      resolveUnitKind(getStepAppliedUnit(step), gUnits) === 'liquid'
  );

// Plant protection, as the cost categories count it: crop protection and
// adjuvants (Insights/costCategories.ts).
const PLANT_PROTECTION_STEP_TYPES = new Set<InputStepType>([
  InputStepType.CropProtection,
  InputStepType.Adjuvant,
]);

/**
 * True for a plant protection product in a liquid unit — the only product
 * whose adding fills in the organisation's default water rate.
 */
export const isLiquidPlantProtectionStep = (
  step: IInputStep,
  gUnits: GUnit[]
): boolean =>
  PLANT_PROTECTION_STEP_TYPES.has(step.stepType) &&
  resolveUnitKind(getStepAppliedUnit(step), gUnits) === 'liquid';

export const isApplicationLikeTask = (
  task: IInputTask
): task is IInputTaskApplication | IInputTaskCropEstablishment =>
  task.taskType === InputTaskType.Application ||
  task.taskType === InputTaskType.CropEstablishment;

// Data-driven default for whether the Application Details card should start
// open on an expanded task row — a task with nothing to show starts
// collapsed behind the task row's "+ Details" pill (OP-2125).
export const taskHasAnyApplicationDetail = (
  task: IInputTask,
  gUnits: GUnit[]
): boolean => {
  if (task.notes) return true;
  if (!isApplicationLikeTask(task)) return false;
  return (
    task.waterRate !== null ||
    task.targetSpecies.length > 0 ||
    taskHasLiquidInput(task, gUnits)
  );
};

export const getRateUnitOptions = (
  stepType: InputStepType,
  gUnits: GUnit[]
): EditableSelectOption[] => {
  const filter = STEP_UNIT_FILTERS[stepType];
  if (!filter) return [];
  return filterGUnitsByFilter(gUnits, filter).map((unit) => ({
    value: unit.globalId,
    label: unit.code,
  }));
};

export const getAreaUnitOptions = (gUnits: GUnit[]): EditableSelectOption[] =>
  filterGUnitsByFilter(gUnits, AREA_UNIT_FILTER).map((unit) => ({
    value: unit.globalId,
    label: unit.code,
  }));

const SEED_DRESS_ITEM_MAX_LENGTH = 30;

// Normalises a SeedDress item name for display in user-facing messages:
// - blank/whitespace-only  → falls back to the generic step-type label
// - longer than 30 chars   → truncated with an ellipsis
const normaliseSeedDressItemName = (item: string): string => {
  const trimmed = item.trim();
  if (!trimmed) return INPUT_STEP_LABEL_SEED_DRESS;
  if (trimmed.length > SEED_DRESS_ITEM_MAX_LENGTH) {
    return `${trimmed.slice(0, SEED_DRESS_ITEM_MAX_LENGTH)}\u2026`;
  }
  return trimmed;
};

// Returns the display names of every SeedDress step in the task whose
// linkedStepId points at the given Seed step. Used to build user-facing
// messages that name the specific treatments blocking an action.
export const getLinkedSeedDressItems = (
  task: IInputTask,
  seedStepId: string
): string[] =>
  task.steps
    .filter(
      (s): s is IInputStepSeedDress =>
        s.stepType === InputStepType.SeedDress && s.linkedStepId === seedStepId
    )
    .map((s) => normaliseSeedDressItemName(s.item));

// Builds a human-readable reason from the names of the blocking SeedDress
// steps. Singular/plural pronoun is resolved automatically.
// suffix: extra clause appended after "first", e.g. " to change step type".
export const buildSeedLinkedReason = (items: string[], suffix = ''): string => {
  const names = items.join(', ');
  const pronoun = items.length === 1 ? 'it' : 'them';
  return `Linked to ${names} \u2014 delete ${pronoun} first${suffix}`;
};

interface LinkableSeed {
  stepId: string;
  item: string;
  item2: string | null;
}

/** Saved Seed steps, then seeds checked in the open picker session (they carry a pre-generated stepId). */
const getLinkableSeeds = (
  task: IInputTask,
  checkedSeeds: StepSelectorOption[]
): LinkableSeed[] => [
  ...task.steps
    .filter(
      (step): step is IInputStepSeed => step.stepType === InputStepType.Seed
    )
    .map((step) => ({
      stepId: step.stepId,
      item: step.item,
      item2: step.item2,
    })),
  ...checkedSeeds.flatMap((seed) =>
    seed.stepId
      ? [{ stepId: seed.stepId, item: seed.item, item2: seed.item2 ?? null }]
      : []
  ),
];

/**
 * "Seed to be treated" options for a Seed Treatment (SeedDress) row.
 * - Saved Seed steps in this task (per OP-2127's AC), labelled "Crop (Variety)".
 * - Plus `checkedSeeds` staged in the current picker session (OP-2436).
 */
export const getLinkedSeedStepOptions = (
  task: IInputTask,
  checkedSeeds: StepSelectorOption[] = []
): EditableSelectOption[] =>
  getLinkableSeeds(task, checkedSeeds).map((seed) => ({
    value: seed.stepId,
    label: seed.item2 ? `${seed.item} (${seed.item2})` : seed.item,
  }));

/**
 * Picker row/chip label for a staged Seed Treatment, e.g. "Vibrance (Wheat, Mace)".
 * Resolved from the linked seed each render so it follows variety changes;
 * falls back to the product label when unlinked or the seed is gone.
 */
export const getSeedTreatmentOptionDisplayLabel = (
  option: StepSelectorOption,
  task: IInputTask,
  checkedSeeds: StepSelectorOption[] = []
): string => {
  if (!option.linkedSeedStepId) return option.label;
  const seed = getLinkableSeeds(task, checkedSeeds).find(
    (candidate) => candidate.stepId === option.linkedSeedStepId
  );
  if (!seed) return option.label;
  const seedLabel = seed.item2 ? `${seed.item}, ${seed.item2}` : seed.item;
  return `${option.label} (${seedLabel})`;
};

export interface StepDisplayLabel {
  primaryLabel: string;
  secondaryLabel?: string | null;
}

export const getStepDisplayLabel = (
  step: IInputStepSeed | IInputStepSeedDress,
  task: IInputTask
): StepDisplayLabel => {
  const primaryLabel = step.item;

  if (step.stepType === InputStepType.Seed)
    return {
      primaryLabel,
      secondaryLabel: step.item2 || SEED_NO_VARIETY_LABEL,
    };

  if (!step.linkedStepId) return { primaryLabel, secondaryLabel: null };
  const seedStep = task.steps.find(
    (candidate): candidate is IInputStepSeed =>
      candidate.stepId === step.linkedStepId &&
      candidate.stepType === InputStepType.Seed
  );

  if (!seedStep) return { primaryLabel, secondaryLabel: null };

  const secondaryLabel = seedStep.item2
    ? `${seedStep.item}, ${seedStep.item2}`
    : seedStep.item;

  return { primaryLabel, secondaryLabel };
};

export const calculateTaskCost = (steps: IInputStep[]): number => {
  return steps.reduce((total, step) => {
    const rate = getStepRate(step);
    const cost = getStepCost(step);
    if (rate === null || cost === null) return total;
    return total + rate * cost;
  }, 0);
};

const DEFAULT_WATER_RATE = 100;

const roundTo = (value: number, precision: number): number => {
  const modifier = 10 ** precision;
  return Math.round(value * modifier) / modifier;
};

export const formatStepCalculation = (value: number | null): string =>
  value === null
    ? ' '
    : value % 1 === 0
      ? String(value)
      : roundTo(value, 2).toFixed(2);

// Per100Kg rate is applied per 100kg of the linked Seed step's sowing rate
// (findLinkedSeedRateKg is the canonical resolver, shared with Gross
// Margin/Summary/the Preview page so this never drifts from theirs);
// PerHa rate applies directly per hectare (multiplier of 1).
const getSeedDressMultiplier = (
  step: IInputStepSeedDress,
  task: IInputTask,
  gUnits: GUnit[]
): number => {
  if (step.rateMode === SeedDressRateMode.PerHa) return 1;
  return findLinkedSeedRateKg(step, task, gUnits) / 100;
};

export const calculateStepRequired = (
  step: IInputStep,
  task: IInputTask,
  input: IInput,
  gUnits: GUnit[]
): number | null => {
  if (NO_CALCULATION_STEP_TYPES.has(step.stepType)) return null;

  const rate = getStepRate(step) ?? 0;
  const area = resolveAreaHa(step, task, input, gUnits);

  if (step.stepType === InputStepType.Adjuvant) {
    const rateInLitres = isMillilitreUnit(getStepUnitLabel(step))
      ? convertMillilitresToLitres(rate)
      : rate;
    return area * DEFAULT_WATER_RATE * (rateInLitres / 100);
  }

  if (step.stepType === InputStepType.SeedDress) {
    return rate * getSeedDressMultiplier(step, task, gUnits) * area;
  }

  return area * rate;
};

export const calculateStepCostPerArea = (
  step: IInputStep,
  task: IInputTask,
  gUnits: GUnit[]
): number | null => {
  if (
    step.stepType === InputStepType.Production ||
    step.stepType === InputStepType.Generic
  )
    return null;

  const rate = getStepRate(step) ?? 0;
  const cost = getStepCost(step) ?? 0;

  if (step.stepType === InputStepType.SeedDress) {
    return rate * getSeedDressMultiplier(step, task, gUnits) * cost;
  }

  return rate * cost;
};
